import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import { trackEvent } from '../../../utils/analytics';
import './AssignmentForm.css';

/**
 * AssignmentForm - Reusable assignment creation/editing form
 * 
 * Can be used in standalone pages or within modals
 * Supports create mode (edit mode deferred for future implementation)
 */
export default function AssignmentForm({
  prefill = {},
  compact = false,
  onCancel,
  onSuccess,
  onDirtyChange,
  renderActions = true
}) {
  const { user } = useAuth();
  const { getEndeavorsByUser, getEndeavorEvents, createAssignment } = useData();

  const initialDate = prefill.selectedDate ? new Date(prefill.selectedDate) : new Date();
  const defaultDueTime = '23:59';

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    assignmentType: 'homework',
    priority: 'medium',
    points: '',
    dueDate: initialDate.toISOString().split('T')[0],
    dueTime: defaultDueTime,
    endeavorId: prefill.endeavorId || '',
    eventId: ''
  });

  const [endeavors, setEndeavors] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [error, setError] = useState(null);

  // Store initial snapshot for dirty tracking
  const initialSnapshot = useMemo(() => JSON.stringify(formData), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track dirty state
  const isDirty = useMemo(() => {
    return JSON.stringify(formData) !== initialSnapshot;
  }, [formData, initialSnapshot]);

  // Notify parent of dirty state changes
  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(isDirty);
    }
  }, [isDirty, onDirtyChange]);

  const assignmentTypes = useMemo(() => [
    { value: 'homework', label: '📝 Homework', icon: '📝' },
    { value: 'project', label: '🔬 Project', icon: '🔬' },
    { value: 'quiz', label: '❓ Quiz', icon: '❓' },
    { value: 'exam', label: '📋 Exam', icon: '📋' },
    { value: 'essay', label: '✍️ Essay', icon: '✍️' },
    { value: 'lab', label: '🧪 Lab', icon: '🧪' },
    { value: 'reading', label: '📖 Reading', icon: '📖' },
    { value: 'discussion', label: '💬 Discussion', icon: '💬' },
    { value: 'presentation', label: '📊 Presentation', icon: '📊' },
    { value: 'other', label: '📌 Other', icon: '📌' }
  ], []);

  const priorities = useMemo(() => [
    { value: 'low', label: '🟢 Low', color: '#10b981' },
    { value: 'medium', label: '🟡 Medium', color: '#f59e0b' },
    { value: 'high', label: '🔴 High', color: '#ef4444' }
  ], []);

  // Load endeavors
  const loadEndeavors = useCallback(async () => {
    try {
      const userId = user?.id || user?.userId;
      if (!userId) return;

      const data = await getEndeavorsByUser(userId);
      const userEndeavors = Array.isArray(data) ? data : (data.content || []);
      setEndeavors(userEndeavors);
      
      // Auto-select first endeavor if none selected
      if (userEndeavors.length > 0 && !formData.endeavorId) {
        setFormData(prev => ({
          ...prev,
          endeavorId: userEndeavors[0].endeavorId || userEndeavors[0].id
        }));
      }
    } catch (error) {
      console.error('Error loading endeavors:', error);
      setError('Failed to load courses. Please try again.');
    }
  }, [user, getEndeavorsByUser, formData.endeavorId]);

  // Load events for selected endeavor
  const loadEvents = useCallback(async (endeavorId) => {
    if (!endeavorId) {
      setEvents([]);
      return;
    }

    setLoadingEvents(true);
    try {
      const data = await getEndeavorEvents(endeavorId);
      const endeavorEvents = Array.isArray(data) ? data : (data.content || []);
      setEvents(endeavorEvents);
    } catch (error) {
      console.error('Error loading events:', error);
      // Don't show error - events are optional
      setEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  }, [getEndeavorEvents]);

  // Initialize
  useEffect(() => {
    loadEndeavors();
  }, [loadEndeavors]);

  // Load events when endeavor changes
  useEffect(() => {
    if (formData.endeavorId) {
      loadEvents(formData.endeavorId);
    } else {
      setEvents([]);
    }
  }, [formData.endeavorId, loadEvents]);

  // Auto-prefill dueDate/dueTime from selected event
  useEffect(() => {
    if (!formData.eventId || events.length === 0) return;

    const selectedEvent = events.find(e => {
      const eventId = e.eventId ?? e.id;
      return String(eventId) === String(formData.eventId);
    });

    if (selectedEvent && selectedEvent.startDatetime) {
      const eventStart = new Date(selectedEvent.startDatetime);
      const prefillDate = eventStart.toISOString().split('T')[0];
      const prefillTime = eventStart.toTimeString().slice(0, 5);

      setFormData(prev => ({
        ...prev,
        dueDate: prefillDate,
        dueTime: prefillTime
      }));
    }
  }, [formData.eventId, events]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Clear eventId when endeavor changes
    if (name === 'endeavorId') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        eventId: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Manual validation
      if (!formData.name.trim()) {
        setError('Assignment name is required.');
        setLoading(false);
        return;
      }

      if (!formData.endeavorId) {
        setError('Please select a course.');
        setLoading(false);
        return;
      }

      if (!formData.dueDate || !formData.dueTime) {
        setError('Due date and time are required.');
        setLoading(false);
        return;
      }

      const dueDatetime = `${formData.dueDate}T${formData.dueTime}:00`;
      const userId = user?.id || user?.userId;

      const assignmentData = {
        title: formData.name,
        description: formData.description,
        dueDatetime: dueDatetime,
        priority: formData.priority.toUpperCase(), // API expects uppercase
        userId: userId,
        endeavorId: formData.endeavorId,
        assignmentType: formData.assignmentType,
        points: formData.points ? parseFloat(formData.points) : null,
        eventId: formData.eventId || null
      };

      const created = await createAssignment(assignmentData);
      
      // DataContext.createAssignment now returns normalized object directly
      // Check if it's still a Response object (fallback for backwards compatibility)
      let normalized;
      if (created && typeof created.json === 'function') {
        // Old Response format
        if (!created.ok) {
          const errorText = await created.text();
          throw new Error(`Failed to create assignment: ${errorText}`);
        }
        const parsed = await created.json();
        const createdId = parsed?.assignmentId ?? parsed?.id ?? parsed?.assignment_id ?? null;
        normalized = { ...parsed, _normalizedId: createdId };
      } else {
        // New normalized format from DataContext
        normalized = created;
      }

      // Track success
      trackEvent?.('calendar_modal_create_success', { type: 'assignment' });

      // Call success handler instead of navigating
      if (onSuccess) {
        onSuccess(normalized);
      }
    } catch (error) {
      console.error('Error creating assignment:', error);
      setError(error.message || 'Failed to create assignment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Empty state when no courses
  if (endeavors.length === 0) {
    return (
      <div className={`assignment-form-empty ${compact ? 'compact' : ''}`}>
        <h3>Create a course first</h3>
        <p>You need at least one course before adding assignments.</p>
        <button
          type="button"
          className="btn-primary"
          onClick={() => window.location.href = '/dashboard'}
        >
          Go to Dashboard
        </button>
      </div>
    );
  }

  return (
    <form 
      id="assignment-form"
      onSubmit={handleSubmit} 
      className={`assignment-form ${compact ? 'compact' : ''}`}
    >
      {error && (
        <div className="error-alert">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      <div className="form-section">
        <h3>📋 Assignment Details</h3>
        
        <div className="form-group">
          <label htmlFor="name">Assignment Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            placeholder="Enter assignment name..."
            autoFocus={compact}
          />
        </div>

        <div className="form-row four-columns">
          <div className="form-group">
            <label htmlFor="assignmentType">Type *</label>
            <select
              id="assignmentType"
              name="assignmentType"
              value={formData.assignmentType}
              onChange={handleInputChange}
              required
            >
              {assignmentTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="priority">Priority *</label>
            <select
              id="priority"
              name="priority"
              value={formData.priority}
              onChange={handleInputChange}
              required
            >
              {priorities.map(priority => (
                <option key={priority.value} value={priority.value}>
                  {priority.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="points">Points</label>
            <input
              type="number"
              id="points"
              name="points"
              value={formData.points}
              onChange={handleInputChange}
              min="0"
              step="0.01"
              placeholder="Optional"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows="4"
            placeholder="Enter assignment description..."
          />
        </div>
      </div>

      <div className="form-section">
        <h3>📚 Course & Event</h3>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="endeavorId">Course *</label>
            <select
              id="endeavorId"
              name="endeavorId"
              value={formData.endeavorId}
              onChange={handleInputChange}
              required
            >
              <option value="">Select a course...</option>
              {endeavors.map(endeavor => (
                <option 
                  key={endeavor.endeavorId || endeavor.id} 
                  value={endeavor.endeavorId || endeavor.id}
                >
                  {endeavor.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="eventId">Related Event</label>
            <select
              id="eventId"
              name="eventId"
              value={formData.eventId}
              onChange={handleInputChange}
              disabled={!formData.endeavorId || loadingEvents}
            >
              <option value="">
                {loadingEvents ? 'Loading events...' : 'None (optional)'}
              </option>
              {events.map(event => (
                <option 
                  key={event.eventId || event.id} 
                  value={event.eventId || event.id}
                >
                  {event.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>📅 Due Date & Time</h3>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="dueDate">Due Date *</label>
            <input
              type="date"
              id="dueDate"
              name="dueDate"
              value={formData.dueDate}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="dueTime">Due Time *</label>
            <input
              type="time"
              id="dueTime"
              name="dueTime"
              value={formData.dueTime}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>
      </div>

      {renderActions && (
        <div className={`form-actions ${compact ? 'sticky' : ''}`}>
          <button type="button" className="btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-create" disabled={loading}>
            {loading ? (
              <>
                <span className="btn-spinner"></span>
                Creating...
              </>
            ) : (
              '✨ Create Assignment'
            )}
          </button>
        </div>
      )}
    </form>
  );
}
